import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import { post, get, patch, del } from 'aws-amplify/api';
import { useAuth } from '../contexts/AuthContext';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './EventCalendar.css';

const localizer = momentLocalizer(moment);

const EventCalendar = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState('calendar'); // 'calendar' or 'requests'

  const [events, setEvents] = useState([]);
  const [roomBookings, setRoomBookings] = useState([]); // Approved private room bookings
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const { getAuthToken } = useAuth();

  // Event Requests state
  const [eventRequests, setEventRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [requestStatusFilter, setRequestStatusFilter] = useState('pending');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [processingRequest, setProcessingRequest] = useState(false);

  // Calendar navigation state
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState('month');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_date: '',
    event_time: '',
    image_url: '',
    color: '#f97316', // Default Chulas orange
    rsvp_enabled: false,
    rsvp_slots: 50,
    rsvp_waitlist_enabled: true,
    is_featured: false,
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  // RSVP management state
  const [showRsvpModal, setShowRsvpModal] = useState(false);
  const [rsvpList, setRsvpList] = useState([]);
  const [rsvpStats, setRsvpStats] = useState(null);
  const [loadingRsvps, setLoadingRsvps] = useState(false);

  // Fetch events on mount
  useEffect(() => {
    fetchEvents();
    fetchApprovedRoomBookings();
    // Also fetch pending requests for badge count
    fetchPendingRequestsCount();
  }, []);

  // Fetch event requests when tab switches to requests or filter changes
  useEffect(() => {
    if (activeTab === 'requests') {
      fetchEventRequests();
    }
  }, [activeTab, requestStatusFilter]);

  // Helper to fetch pending count for badge
  const fetchPendingRequestsCount = async () => {
    try {
      const token = await getAuthToken();
      const restOperation = get({
        apiName: 'ChulasAdminAPI',
        path: '/v1/admin/event-requests?status=pending',
        options: {
          headers: {
            Authorization: token,
          },
        },
      });
      const response = await restOperation.response;
      const data = await response.body.json();
      // Only update if we're not already on the requests tab
      if (activeTab !== 'requests') {
        setEventRequests(data.requests || []);
      }
    } catch (err) {
      console.error('Error fetching pending requests count:', err);
    }
  };

  // Fetch approved private room bookings for calendar display
  const fetchApprovedRoomBookings = async () => {
    try {
      const token = await getAuthToken();
      const restOperation = get({
        apiName: 'ChulasAdminAPI',
        path: '/v1/admin/event-requests?status=approved',
        options: {
          headers: {
            Authorization: token,
          },
        },
      });
      const response = await restOperation.response;
      const data = await response.body.json();

      // Filter for private room bookings and transform for calendar
      const privateBookings = (data.requests || [])
        .filter(req => req.request_type === 'private')
        .map(booking => {
          // Determine color based on room
          let color = '#9333ea'; // Purple default for private rooms
          if (booking.room_preference === 'jalisco') {
            color = '#7c3aed'; // Violet for Jalisco
          } else if (booking.room_preference === 'zacatecas') {
            color = '#a855f7'; // Light purple for Zacatecas
          } else if (booking.room_preference === 'mezzanine') {
            color = '#84cc16'; // Lime for Entire Mezzanine
          }

          // Create start and end times
          const startTime = booking.start_time || '12:00';
          const endTime = booking.end_time || '14:00';

          return {
            id: `booking-${booking.request_id}`,
            title: `🔒 ${booking.event_name}${booking.room_preference ? ` (${booking.room_preference === 'jalisco' ? 'Jalisco' : booking.room_preference === 'zacatecas' ? 'Zacatecas' : 'Entire Mezzanine'})` : ''}`,
            start: new Date(`${booking.event_date}T${startTime}:00`),
            end: new Date(`${booking.event_date}T${endTime}:00`),
            color: color,
            isRoomBooking: true,
            bookingData: booking,
            allDay: false,
          };
        });

      setRoomBookings(privateBookings);
    } catch (err) {
      console.error('Error fetching approved room bookings:', err);
    }
  };

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError(null);

      const restOperation = get({
        apiName: 'ChulasAdminAPI',
        path: '/v1/events?days=365', // Fetch events for the next year
      });

      const response = await restOperation.response;
      const data = await response.body.json();

      // Transform events for react-big-calendar
      const transformedEvents = (data.events || []).map(event => ({
        id: event.event_id,
        title: event.is_featured ? `⭐ ${event.title}` : event.title,
        rawTitle: event.title,
        start: new Date(event.event_date + (event.event_time ? `T${event.event_time}` : 'T12:00:00')),
        end: new Date(event.event_date + (event.event_time ? `T${event.event_time}` : 'T12:00:00')),
        description: event.description,
        event_date: event.event_date,
        event_time: event.event_time,
        image_url: event.image_url,
        color: event.color || '#f97316', // Default to Chulas orange
        rsvp_enabled: event.rsvp_enabled || false,
        rsvp_slots: event.rsvp_slots || 0,
        rsvp_waitlist_enabled: event.rsvp_waitlist_enabled || false,
        is_featured: event.is_featured || false,
        allDay: !event.event_time, // If no time specified, treat as all-day
      }));

      setEvents(transformedEvents);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError('Failed to fetch events');
    } finally {
      setLoading(false);
    }
  };

  // Event Requests Functions
  const fetchEventRequests = async () => {
    try {
      setRequestsLoading(true);
      setError(null);

      const token = await getAuthToken();
      const queryParam = requestStatusFilter !== 'all' ? `?status=${requestStatusFilter}` : '';

      const restOperation = get({
        apiName: 'ChulasAdminAPI',
        path: `/v1/admin/event-requests${queryParam}`,
        options: {
          headers: {
            Authorization: token,
          },
        },
      });

      const response = await restOperation.response;
      const data = await response.body.json();

      setEventRequests(data.requests || []);
    } catch (err) {
      console.error('Error fetching event requests:', err);
      setError('Failed to fetch event requests');
    } finally {
      setRequestsLoading(false);
    }
  };

  const openRequestModal = (request) => {
    setSelectedRequest(request);
    setAdminNotes(request.admin_notes || '');
    setShowRequestModal(true);
  };

  const closeRequestModal = () => {
    setShowRequestModal(false);
    setSelectedRequest(null);
    setAdminNotes('');
    setError(null);
    setSuccessMessage(null);
  };

  const handleUpdateRequestStatus = async (status) => {
    if (!selectedRequest) return;

    try {
      setProcessingRequest(true);
      setError(null);

      const token = await getAuthToken();

      const patchOperation = patch({
        apiName: 'ChulasAdminAPI',
        path: `/v1/admin/event-requests/${selectedRequest.request_id}`,
        options: {
          headers: {
            Authorization: token,
          },
          body: {
            status,
            admin_notes: adminNotes.trim() || undefined,
          },
        },
      });

      await patchOperation.response;
      setSuccessMessage(`Request ${status} successfully!`);

      // Refresh the list
      await fetchEventRequests();

      // Refresh room bookings on calendar if status changed
      if (status === 'approved' || status === 'rejected') {
        await fetchApprovedRoomBookings();
      }

      // Close modal after a brief delay
      setTimeout(() => {
        closeRequestModal();
      }, 1500);
    } catch (err) {
      console.error('Error updating request status:', err);
      setError('Failed to update request status');
    } finally {
      setProcessingRequest(false);
    }
  };

  const openCreateModal = (slotInfo) => {
    // Format date for input field
    const selectedDate = moment(slotInfo.start).format('YYYY-MM-DD');

    setModalMode('create');
    setSelectedEvent(null);
    setFormData({
      title: '',
      description: '',
      event_date: selectedDate,
      event_time: '',
      image_url: '',
      color: '#f97316', // Default Chulas orange
      rsvp_enabled: false,
      rsvp_slots: 50,
      rsvp_waitlist_enabled: true,
      is_featured: false,
    });
    setImageFile(null);
    setImagePreview(null);
    setShowModal(true);
  };

  const openEditModal = (event) => {
    setModalMode('edit');
    setSelectedEvent(event);
    setFormData({
      title: event.rawTitle || event.title,
      description: event.description || '',
      event_date: event.event_date,
      event_time: event.event_time || '',
      image_url: event.image_url || '',
      color: event.color || '#f97316', // Default to Chulas orange if no color set
      rsvp_enabled: event.rsvp_enabled || false,
      rsvp_slots: event.rsvp_slots || 50,
      rsvp_waitlist_enabled: event.rsvp_waitlist_enabled !== undefined ? event.rsvp_waitlist_enabled : true,
      is_featured: event.is_featured || false,
    });
    setImagePreview(event.image_url || null);
    setImageFile(null);
    setShowModal(true);

    // Fetch RSVP stats if RSVP is enabled for this event
    if (event.rsvp_enabled) {
      fetchRsvps(event.id);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setModalMode('create');
    setSelectedEvent(null);
    setFormData({
      title: '',
      description: '',
      event_date: '',
      event_time: '',
      image_url: '',
      color: '#f97316', // Default Chulas orange
      rsvp_enabled: false,
      rsvp_slots: 50,
      rsvp_waitlist_enabled: true,
      is_featured: false,
    });
    setImageFile(null);
    setImagePreview(null);
    setError(null);
    setSuccessMessage(null);
    // Clear RSVP stats
    setRsvpStats(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseInt(value) || 0 : value)
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }

      setImageFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async (eventId) => {
    if (!imageFile) return null;

    try {
      setUploadingImage(true);

      // Convert image to base64
      const reader = new FileReader();
      const base64Promise = new Promise((resolve, reject) => {
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(imageFile);
      });

      const base64Data = await base64Promise;

      // Upload to backend
      const token = await getAuthToken();
      const restOperation = post({
        apiName: 'ChulasAdminAPI',
        path: `/v1/admin/events/${eventId}/upload-image`,
        options: {
          headers: {
            Authorization: token,
          },
          body: {
            image_data: base64Data,
            content_type: imageFile.type,
          },
        },
      });

      const response = await restOperation.response;
      const data = await response.body.json();

      return data.image_url;
    } catch (err) {
      console.error('Error uploading image:', err);
      throw new Error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim() || !formData.event_date) {
      setError('Title and date are required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);

      const token = await getAuthToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      if (modalMode === 'create') {
        // Create event
        const createOperation = post({
          apiName: 'ChulasAdminAPI',
          path: '/v1/admin/events',
          options: {
            headers: {
              Authorization: token,
            },
            body: {
              title: formData.title.trim(),
              description: formData.description.trim(),
              event_date: formData.event_date,
              event_time: formData.event_time || null,
              color: formData.color,
              rsvp_enabled: formData.rsvp_enabled,
              rsvp_slots: formData.rsvp_enabled ? formData.rsvp_slots : undefined,
              rsvp_waitlist_enabled: formData.rsvp_enabled ? formData.rsvp_waitlist_enabled : undefined,
              is_featured: formData.is_featured,
            },
          },
        });

        const createResponse = await createOperation.response;
        const createData = await createResponse.body.json();

        // Upload image if selected
        if (imageFile) {
          await uploadImage(createData.event.event_id);
        }

        setSuccessMessage('Event created successfully!');
      } else if (modalMode === 'edit') {
        // Upload image first if selected
        if (imageFile) {
          const imageUrl = await uploadImage(selectedEvent.id);
          formData.image_url = imageUrl;
        }

        // Update event
        const updateOperation = patch({
          apiName: 'ChulasAdminAPI',
          path: `/v1/admin/events/${selectedEvent.id}`,
          options: {
            headers: {
              Authorization: token,
            },
            body: {
              title: formData.title.trim(),
              description: formData.description.trim(),
              event_date: formData.event_date,
              event_time: formData.event_time || null,
              color: formData.color,
              ...(formData.image_url && { image_url: formData.image_url }),
              rsvp_enabled: formData.rsvp_enabled,
              rsvp_slots: formData.rsvp_enabled ? formData.rsvp_slots : undefined,
              rsvp_waitlist_enabled: formData.rsvp_enabled ? formData.rsvp_waitlist_enabled : undefined,
              is_featured: formData.is_featured,
            },
          },
        });

        await updateOperation.response;
        setSuccessMessage('Event updated successfully!');

        // Refresh RSVP stats if RSVP is enabled
        if (formData.rsvp_enabled) {
          await fetchRsvps(selectedEvent.id);
        }
      }

      // Refresh events list
      await fetchEvents();

      // Close modal after a brief delay
      setTimeout(() => {
        closeModal();
      }, 1500);
    } catch (err) {
      console.error('Error saving event:', err);
      setError(err.message || 'Failed to save event. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this event?')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const token = await getAuthToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      const deleteOperation = del({
        apiName: 'ChulasAdminAPI',
        path: `/v1/admin/events/${selectedEvent.id}`,
        options: {
          headers: {
            Authorization: token,
          },
        },
      });

      await deleteOperation.response;
      setSuccessMessage('Event deleted successfully!');

      // Refresh events list
      await fetchEvents();

      // Close modal
      setTimeout(() => {
        closeModal();
      }, 1500);
    } catch (err) {
      console.error('Error deleting event:', err);
      setError(err.message || 'Failed to delete event. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // RSVP Management Functions
  const fetchRsvps = async (eventId) => {
    try {
      setLoadingRsvps(true);
      const token = await getAuthToken();

      const restOperation = get({
        apiName: 'ChulasAdminAPI',
        path: `/v1/admin/events/${eventId}/rsvps`,
        options: {
          headers: {
            Authorization: token,
          },
        },
      });

      const response = await restOperation.response;
      const data = await response.body.json();

      setRsvpList(data.rsvps || []);
      setRsvpStats(data.status_counts || {});
    } catch (err) {
      console.error('Error fetching RSVPs:', err);
      setError('Failed to fetch RSVPs');
    } finally {
      setLoadingRsvps(false);
    }
  };

  const openRsvpModal = (event) => {
    if (!event.rsvp_enabled) {
      alert('RSVPs are not enabled for this event');
      return;
    }
    setSelectedEvent(event);
    setShowRsvpModal(true);
    fetchRsvps(event.id);
  };

  const closeRsvpModal = () => {
    setShowRsvpModal(false);
    setSelectedEvent(null);
    setRsvpList([]);
    setRsvpStats(null);
  };

  const handleCancelRsvp = async (userId) => {
    if (!window.confirm('Are you sure you want to cancel this user\'s RSVP?')) {
      return;
    }

    try {
      const token = await getAuthToken();

      const deleteOperation = del({
        apiName: 'ChulasAdminAPI',
        path: `/v1/admin/events/${selectedEvent.id}/rsvps/${userId}`,
        options: {
          headers: {
            Authorization: token,
          },
        },
      });

      await deleteOperation.response;

      // Refresh RSVP list
      fetchRsvps(selectedEvent.id);
      setSuccessMessage('RSVP cancelled successfully');

      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      console.error('Error cancelling RSVP:', err);
      setError('Failed to cancel RSVP');
    }
  };

  // Helper to get events and bookings for a specific date
  const getScheduleForDate = useCallback((dateString) => {
    const targetDate = moment(dateString).format('YYYY-MM-DD');

    // Get public events for this date
    const eventsOnDate = events.filter(event =>
      moment(event.start).format('YYYY-MM-DD') === targetDate
    );

    // Get room bookings for this date (from approved requests)
    const bookingsOnDate = roomBookings.filter(booking =>
      moment(booking.start).format('YYYY-MM-DD') === targetDate
    );

    return { eventsOnDate, bookingsOnDate };
  }, [events, roomBookings]);

  // Check for room conflicts
  const checkRoomConflicts = useCallback((requestDate, roomPreference, startTime, endTime) => {
    const { bookingsOnDate } = getScheduleForDate(requestDate);

    if (!roomPreference || bookingsOnDate.length === 0) {
      return { hasConflict: false, conflicts: [] };
    }

    const conflicts = bookingsOnDate.filter(booking => {
      const bookingRoom = booking.bookingData?.room_preference;

      // Mezzanine conflicts with everything (jalisco, zacatecas, or mezzanine)
      if (roomPreference === 'mezzanine') {
        return true; // Any room booking is a conflict
      }

      // Individual room conflicts with same room or mezzanine
      if (bookingRoom === roomPreference || bookingRoom === 'mezzanine') {
        return true;
      }

      return false;
    });

    return { hasConflict: conflicts.length > 0, conflicts };
  }, [getScheduleForDate]);

  // Custom event style
  const eventStyleGetter = (event) => {
    const style = {
      backgroundColor: event.color || '#f97316', // Use event's custom color or default to Chulas orange
      borderRadius: '5px',
      opacity: 0.8,
      color: 'white',
      border: '0px',
      display: 'block'
    };
    return { style };
  };

  return (
    <div className="event-calendar-container">
      <h1>Event Calendar</h1>
      <p className="page-description">
        Create and manage events for the "What's Happening" section of the mobile app.
      </p>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button
          className={`tab-button ${activeTab === 'calendar' ? 'active' : ''}`}
          onClick={() => setActiveTab('calendar')}
        >
          Calendar
        </button>
        <button
          className={`tab-button ${activeTab === 'requests' ? 'active' : ''}`}
          onClick={() => setActiveTab('requests')}
        >
          Event Requests
          {eventRequests.filter(r => r.status === 'pending').length > 0 && activeTab !== 'requests' && (
            <span className="badge">{eventRequests.filter(r => r.status === 'pending').length}</span>
          )}
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {/* Calendar Tab */}
      {activeTab === 'calendar' && (
        <div className="calendar-wrapper">
          {/* Legend for calendar */}
          <div className="calendar-legend">
            <span className="legend-item"><span className="legend-color" style={{backgroundColor: '#f97316'}}></span> Events</span>
            <span className="legend-item"><span className="legend-color" style={{backgroundColor: '#7c3aed'}}></span> Jalisco Room</span>
            <span className="legend-item"><span className="legend-color" style={{backgroundColor: '#a855f7'}}></span> Zacatecas Room</span>
            <span className="legend-item"><span className="legend-color" style={{backgroundColor: '#84cc16'}}></span> Entire Mezzanine</span>
          </div>
          <Calendar
          localizer={localizer}
          events={[...events, ...roomBookings]}
          startAccessor="start"
          endAccessor="end"
          style={{ height: 700 }}
          onSelectSlot={openCreateModal}
          onSelectEvent={(event) => {
            if (event.isRoomBooking) {
              // Open the request modal for room bookings
              openRequestModal(event.bookingData);
            } else {
              openEditModal(event);
            }
          }}
          selectable
          eventPropGetter={eventStyleGetter}
          views={['month', 'week', 'day']}
          view={currentView}
          onView={setCurrentView}
          date={currentDate}
          onNavigate={setCurrentDate}
        />
        </div>
      )}

      {/* Event Requests Tab */}
      {activeTab === 'requests' && (
        <div className="requests-wrapper">
          {/* Status Filter */}
          <div className="filter-bar">
            <label>Filter by Status:</label>
            <select
              value={requestStatusFilter}
              onChange={(e) => setRequestStatusFilter(e.target.value)}
            >
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="all">All</option>
            </select>
          </div>

          {requestsLoading ? (
            <div className="loading">Loading event requests...</div>
          ) : eventRequests.length === 0 ? (
            <div className="empty-state">
              <p>No {requestStatusFilter !== 'all' ? requestStatusFilter : ''} event requests found.</p>
            </div>
          ) : (
            <div className="requests-list">
              {eventRequests.map((request) => (
                <div
                  key={request.request_id}
                  className={`request-card request-${request.status}`}
                  onClick={() => openRequestModal(request)}
                >
                  <div className="request-card-header">
                    <span className={`request-type-badge ${request.request_type}`}>
                      {request.request_type === 'private' ? 'Private Room' : 'Perform at Chula\'s'}
                    </span>
                    <span className={`status-badge status-${request.status}`}>
                      {request.status}
                    </span>
                  </div>
                  <h3>{request.event_name}</h3>
                  <div className="request-card-meta">
                    <span>
                      <strong>Date:</strong> {moment(request.event_date).format('MMM D, YYYY')}
                      {request.request_type === 'private' && request.start_time && request.end_time
                        ? ` (${request.start_time} - ${request.end_time})`
                        : request.event_time && ` at ${request.event_time}`}
                    </span>
                    {request.request_type === 'private' && request.guest_count && (
                      <span><strong>Guests:</strong> {request.guest_count}</span>
                    )}
                    {request.room_preference && (
                      <span><strong>Room:</strong> {request.room_preference === 'jalisco' ? 'Jalisco Room' : request.room_preference === 'zacatecas' ? 'Zacatecas Room' : 'Entire Mezzanine'}</span>
                    )}
                  </div>
                  <p className="request-description">{request.description}</p>
                  <div className="request-card-footer">
                    <span><strong>Contact:</strong> {request.contact_email}</span>
                    <span className="request-date">Submitted {moment(request.created_at).format('MMM D, YYYY h:mm A')}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{modalMode === 'create' ? 'Create New Event' : 'Edit Event'}</h2>
              <button className="modal-close" onClick={closeModal}>&times;</button>
            </div>

            <form onSubmit={handleSubmit} className="event-form">
              <div className="form-group">
                <label htmlFor="title">Event Title *</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Enter event title..."
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter event description..."
                  rows="4"
                  disabled={loading}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="event_date">Date *</label>
                  <input
                    type="date"
                    id="event_date"
                    name="event_date"
                    value={formData.event_date}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="event_time">Time</label>
                  <input
                    type="time"
                    id="event_time"
                    name="event_time"
                    value={formData.event_time}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="image">Event Image</label>
                <input
                  type="file"
                  id="image"
                  accept="image/*"
                  onChange={handleImageChange}
                  disabled={loading}
                />
                <small>Max size: 5MB. Supported formats: JPG, PNG, GIF</small>

                {imagePreview && (
                  <div className="image-preview">
                    <img src={imagePreview} alt="Event preview" />
                  </div>
                )}
              </div>

              {/* Color Picker */}
              <div className="form-group">
                <label htmlFor="color">Event Color</label>
                <div className="color-picker-wrapper">
                  <div className="color-presets">
                    {[
                      { name: 'Lime', color: '#D6DE23' },
                      { name: 'Orange', color: '#F8A054' },
                      { name: 'Red', color: '#F05960' },
                      { name: 'Purple', color: '#7060A9' },
                      { name: 'Teal', color: '#00B29C' },
                    ].map((preset) => (
                      <button
                        key={preset.color}
                        type="button"
                        className={`color-preset-btn ${formData.color.toUpperCase() === preset.color.toUpperCase() ? 'selected' : ''}`}
                        style={{ backgroundColor: preset.color }}
                        onClick={() => setFormData(prev => ({ ...prev, color: preset.color }))}
                        disabled={loading}
                        title={preset.name}
                      >
                        {formData.color.toUpperCase() === preset.color.toUpperCase() && (
                          <span className="color-check">✓</span>
                        )}
                      </button>
                    ))}
                  </div>
                  <div className="color-custom">
                    <input
                      type="color"
                      id="color"
                      name="color"
                      value={formData.color}
                      onChange={handleInputChange}
                      disabled={loading}
                      className="color-input-native"
                    />
                    <input
                      type="text"
                      value={formData.color}
                      onChange={handleInputChange}
                      name="color"
                      placeholder="#F8A054"
                      disabled={loading}
                      className="color-input-text"
                    />
                  </div>
                </div>
                <small>Select a Chula's brand color or choose a custom color</small>
              </div>

              {/* Featured Event Toggle */}
              <div className="form-group">
                <div className="checkbox-group">
                  <input
                    type="checkbox"
                    id="is_featured"
                    name="is_featured"
                    checked={formData.is_featured}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                  <label htmlFor="is_featured">Feature on Home Screen</label>
                </div>
                <small>Featured events will appear in "What's Happening" on the mobile app home screen</small>
              </div>

              {/* RSVP Settings */}
              <div className="form-group">
                <div className="checkbox-group">
                  <input
                    type="checkbox"
                    id="rsvp_enabled"
                    name="rsvp_enabled"
                    checked={formData.rsvp_enabled}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                  <label htmlFor="rsvp_enabled">Enable RSVPs for this event</label>
                </div>
              </div>

              {formData.rsvp_enabled && (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="rsvp_slots">Number of Slots *</label>
                      <input
                        type="number"
                        id="rsvp_slots"
                        name="rsvp_slots"
                        value={formData.rsvp_slots}
                        onChange={handleInputChange}
                        min="1"
                        required={formData.rsvp_enabled}
                        disabled={loading}
                      />
                    </div>

                    <div className="form-group">
                      <div className="checkbox-group">
                        <input
                          type="checkbox"
                          id="rsvp_waitlist_enabled"
                          name="rsvp_waitlist_enabled"
                          checked={formData.rsvp_waitlist_enabled}
                          onChange={handleInputChange}
                          disabled={loading}
                        />
                        <label htmlFor="rsvp_waitlist_enabled">Enable Waitlist</label>
                      </div>
                    </div>
                  </div>

                  {modalMode === 'edit' && (
                    <div className="form-group">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => openRsvpModal(selectedEvent)}
                        disabled={loading}
                      >
                        Manage RSVPs ({rsvpStats?.confirmed || 0} confirmed, {rsvpStats?.waitlist || 0} waitlist)
                      </button>
                    </div>
                  )}
                </>
              )}

              {error && (
                <div className="alert alert-error">
                  {error}
                </div>
              )}

              {successMessage && (
                <div className="alert alert-success">
                  {successMessage}
                </div>
              )}

              <div className="modal-actions">
                {modalMode === 'edit' && (
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleDelete}
                    disabled={loading}
                  >
                    Delete Event
                  </button>
                )}
                <div className="modal-actions-right">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeModal}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading || uploadingImage}
                  >
                    {loading || uploadingImage
                      ? uploadingImage ? 'Uploading Image...' : 'Saving...'
                      : modalMode === 'create' ? 'Create Event' : 'Update Event'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* RSVP Management Modal */}
      {showRsvpModal && selectedEvent && (
        <div className="modal-overlay" onClick={closeRsvpModal}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Manage RSVPs - {selectedEvent.title}</h2>
              <button className="modal-close" onClick={closeRsvpModal}>&times;</button>
            </div>

            {successMessage && (
              <div className="alert alert-success">
                {successMessage}
              </div>
            )}

            {error && (
              <div className="alert alert-error">
                {error}
              </div>
            )}

            {loadingRsvps ? (
              <div className="loading">Loading RSVPs...</div>
            ) : (
              <>
                {/* RSVP Stats */}
                {rsvpStats && (
                  <div className="rsvp-stats">
                    <div className="stat-card">
                      <h4>Total Slots</h4>
                      <p className="stat-number">{selectedEvent.rsvp_slots}</p>
                    </div>
                    <div className="stat-card">
                      <h4>Confirmed</h4>
                      <p className="stat-number confirmed">{rsvpStats.confirmed || 0}</p>
                    </div>
                    <div className="stat-card">
                      <h4>Waitlist</h4>
                      <p className="stat-number waitlist">{rsvpStats.waitlist || 0}</p>
                    </div>
                    <div className="stat-card">
                      <h4>Cancelled</h4>
                      <p className="stat-number cancelled">{rsvpStats.cancelled || 0}</p>
                    </div>
                    <div className="stat-card">
                      <h4>Available</h4>
                      <p className="stat-number available">
                        {selectedEvent.rsvp_slots - (rsvpStats.confirmed || 0)}
                      </p>
                    </div>
                  </div>
                )}

                {/* RSVP List */}
                {rsvpList.length === 0 ? (
                  <div className="empty-state">
                    <p>No RSVPs yet for this event.</p>
                  </div>
                ) : (
                  <div className="rsvp-list">
                    <table className="rsvp-table">
                      <thead>
                        <tr>
                          <th>Status</th>
                          <th>Email</th>
                          <th>Phone</th>
                          <th>Notes</th>
                          <th>RSVP Date</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rsvpList.map((rsvp) => (
                          <tr key={rsvp.user_id} className={`rsvp-row rsvp-${rsvp.status}`}>
                            <td>
                              <span className={`status-badge status-${rsvp.status}`}>
                                {rsvp.status}
                              </span>
                            </td>
                            <td>{rsvp.user_email || 'N/A'}</td>
                            <td>{rsvp.user_phone || 'N/A'}</td>
                            <td className="notes-cell">{rsvp.notes || '-'}</td>
                            <td>{new Date(rsvp.created_at).toLocaleString()}</td>
                            <td>
                              {rsvp.status !== 'cancelled' && (
                                <button
                                  className="btn btn-sm btn-danger"
                                  onClick={() => handleCancelRsvp(rsvp.user_id)}
                                >
                                  Cancel
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}

            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeRsvpModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Event Request Detail Modal */}
      {showRequestModal && selectedRequest && (
        <div className="modal-overlay" onClick={closeRequestModal}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Event Request Details</h2>
              <button className="modal-close" onClick={closeRequestModal}>&times;</button>
            </div>

            <div className="request-detail-content">
              {/* Status Banner */}
              <div className={`status-banner status-${selectedRequest.status}`}>
                <span className={`request-type-badge ${selectedRequest.request_type}`}>
                  {selectedRequest.request_type === 'private' ? 'Private Room' : 'Perform at Chula\'s'}
                </span>
                <span className={`status-badge status-${selectedRequest.status}`}>
                  {selectedRequest.status.toUpperCase()}
                </span>
              </div>

              {/* Event Details */}
              <div className="request-detail-section">
                <h3>{selectedRequest.event_name}</h3>

                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Requested Date</label>
                    <span>{moment(selectedRequest.event_date).format('dddd, MMMM D, YYYY')}</span>
                  </div>
                  {selectedRequest.request_type === 'private' && selectedRequest.start_time && selectedRequest.end_time ? (
                    <div className="detail-item">
                      <label>Requested Time</label>
                      <span>{selectedRequest.start_time} - {selectedRequest.end_time}</span>
                    </div>
                  ) : selectedRequest.event_time && (
                    <div className="detail-item">
                      <label>Requested Time</label>
                      <span>{selectedRequest.event_time}</span>
                    </div>
                  )}
                  {selectedRequest.request_type === 'private' && (
                    <>
                      <div className="detail-item">
                        <label>Number of Guests</label>
                        <span>{selectedRequest.guest_count || 'Not specified'}</span>
                      </div>
                      {selectedRequest.room_preference && (
                        <div className="detail-item">
                          <label>Room Preference</label>
                          <span>
                            {selectedRequest.room_preference === 'jalisco'
                              ? 'The Jalisco Room (Capacity: 60)'
                              : selectedRequest.room_preference === 'zacatecas'
                              ? 'The Zacatecas Room (Capacity: 34)'
                              : 'Entire Mezzanine (Capacity: 94)'}
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="detail-item full-width">
                  <label>Description</label>
                  <p className="description-text">{selectedRequest.description}</p>
                </div>
              </div>

              {/* Schedule for Requested Date - Conflict Check */}
              {(() => {
                const { eventsOnDate, bookingsOnDate } = getScheduleForDate(selectedRequest.event_date);
                const { hasConflict, conflicts } = selectedRequest.request_type === 'private'
                  ? checkRoomConflicts(selectedRequest.event_date, selectedRequest.room_preference)
                  : { hasConflict: false, conflicts: [] };

                return (
                  <div className={`request-detail-section schedule-section ${hasConflict ? 'has-conflict' : ''}`}>
                    <h4>
                      Schedule for {moment(selectedRequest.event_date).format('MMMM D, YYYY')}
                      {hasConflict && <span className="conflict-warning"> - Conflict Detected!</span>}
                    </h4>

                    {eventsOnDate.length === 0 && bookingsOnDate.length === 0 ? (
                      <p className="no-schedule">No events or room bookings scheduled for this date.</p>
                    ) : (
                      <div className="schedule-list">
                        {/* Public Events */}
                        {eventsOnDate.length > 0 && (
                          <div className="schedule-group">
                            <label>Public Events</label>
                            {eventsOnDate.map(event => (
                              <div key={event.id} className="schedule-item event-item">
                                <span className="schedule-color" style={{ backgroundColor: event.color || '#f97316' }}></span>
                                <div className="schedule-info">
                                  <strong>{event.title}</strong>
                                  {event.event_time && <span className="schedule-time">{event.event_time}</span>}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Room Bookings */}
                        {bookingsOnDate.length > 0 && (
                          <div className="schedule-group">
                            <label>Room Bookings</label>
                            {bookingsOnDate.map(booking => {
                              const isConflicting = conflicts.some(c => c.id === booking.id);
                              const roomName = booking.bookingData?.room_preference === 'jalisco'
                                ? 'Jalisco Room'
                                : booking.bookingData?.room_preference === 'zacatecas'
                                ? 'Zacatecas Room'
                                : 'Entire Mezzanine';

                              return (
                                <div key={booking.id} className={`schedule-item booking-item ${isConflicting ? 'conflicting' : ''}`}>
                                  <span className="schedule-color" style={{ backgroundColor: booking.color }}></span>
                                  <div className="schedule-info">
                                    <strong>{booking.bookingData?.event_name}</strong>
                                    <span className="room-badge">{roomName}</span>
                                    {booking.bookingData?.start_time && booking.bookingData?.end_time && (
                                      <span className="schedule-time">
                                        {booking.bookingData.start_time} - {booking.bookingData.end_time}
                                      </span>
                                    )}
                                    {isConflicting && <span className="conflict-badge">CONFLICT</span>}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Contact Information */}
              <div className="request-detail-section">
                <h4>Contact Information</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Email</label>
                    <span>{selectedRequest.contact_email}</span>
                  </div>
                  {selectedRequest.contact_phone && (
                    <div className="detail-item">
                      <label>Phone</label>
                      <span>{selectedRequest.contact_phone}</span>
                    </div>
                  )}
                </div>
                <div className="detail-item">
                  <label>Submitted</label>
                  <span>{moment(selectedRequest.created_at).format('MMMM D, YYYY [at] h:mm A')}</span>
                </div>
              </div>

              {/* Admin Notes */}
              <div className="request-detail-section">
                <h4>Admin Notes</h4>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add notes for the requester (will be visible to them)..."
                  rows="3"
                  disabled={selectedRequest.status !== 'pending' || processingRequest}
                />
              </div>

              {error && (
                <div className="alert alert-error">
                  {error}
                </div>
              )}

              {successMessage && (
                <div className="alert alert-success">
                  {successMessage}
                </div>
              )}

              {/* Actions */}
              {(() => {
                const { hasConflict, conflicts } = selectedRequest.request_type === 'private'
                  ? checkRoomConflicts(selectedRequest.event_date, selectedRequest.room_preference)
                  : { hasConflict: false, conflicts: [] };

                const handleApproveWithConflictCheck = () => {
                  if (hasConflict) {
                    const conflictDetails = conflicts.map(c => {
                      const room = c.bookingData?.room_preference === 'jalisco'
                        ? 'Jalisco Room'
                        : c.bookingData?.room_preference === 'zacatecas'
                        ? 'Zacatecas Room'
                        : 'Entire Mezzanine';
                      return `- ${c.bookingData?.event_name} (${room})`;
                    }).join('\n');

                    const confirmed = window.confirm(
                      `⚠️ CONFLICT DETECTED!\n\nThis request conflicts with existing bookings:\n${conflictDetails}\n\nAre you sure you want to approve this request anyway?`
                    );
                    if (!confirmed) return;
                  }
                  handleUpdateRequestStatus('approved');
                };

                return (
                  <div className="modal-actions request-actions">
                    {selectedRequest.status === 'pending' ? (
                      <>
                        <button
                          type="button"
                          className="btn btn-danger"
                          onClick={() => handleUpdateRequestStatus('rejected')}
                          disabled={processingRequest}
                        >
                          {processingRequest ? 'Processing...' : 'Reject Request'}
                        </button>
                        <div className="modal-actions-right">
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={closeRequestModal}
                            disabled={processingRequest}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className={`btn ${hasConflict ? 'btn-warning' : 'btn-success'}`}
                            onClick={handleApproveWithConflictCheck}
                            disabled={processingRequest}
                          >
                            {processingRequest ? 'Processing...' : hasConflict ? '⚠️ Approve (Conflict)' : 'Approve Request'}
                          </button>
                        </div>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={closeRequestModal}
                      >
                        Close
                      </button>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventCalendar;
